/** Lightweight markdown for legal pages (headings, bold, lists, paragraphs). */

export type TextAlign = "left" | "center" | "right";

export type MarkdownBlock =
  | { type: "heading"; level: 1 | 2 | 3; text: string; align?: TextAlign }
  | { type: "paragraph"; text: string; align?: TextAlign }
  | { type: "bullet"; text: string };

export type InlineRun = { text: string; bold?: boolean; italic?: boolean; code?: boolean };

const headingRe = /^(#{1,3})\s+(.+)$/;
const headingAlignRe = /^(#{1,3})\s+\{align:(left|center|right)\}\s+(.+)$/;
const bulletRe = /^[-*]\s+(.+)$/;
const paraAlignRe = /^\{align:(left|center|right)\}\s+(.+)$/;

export function formatAlignPrefix(align: TextAlign | undefined): string {
  return align && align !== "left" ? `{align:${align}} ` : "";
}

export function alignMarkdownLine(text: string, align: TextAlign | undefined): string {
  if (!align || align === "left") return text;
  return `{align:${align}} ${text}`;
}

/** Plain text only (no markers). */
export function stripMarkdownInline(text: string): string {
  return text.replace(/\*\*(.+?)\*\*/g, "$1").replace(/_(.+?)_/g, "$1").replace(/`([^`]+)`/g, "$1");
}

export function parseMarkdownBlocks(source: string): MarkdownBlock[] {
  const lines = source.replace(/\r\n/g, "\n").split("\n");
  const blocks: MarkdownBlock[] = [];
  let para: string[] = [];
  let paraAlign: TextAlign | undefined;

  const flushPara = () => {
    if (!para.length) return;
    blocks.push({ type: "paragraph", text: para.join(" "), ...(paraAlign ? { align: paraAlign } : {}) });
    para = [];
    paraAlign = undefined;
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line.trim()) {
      flushPara();
      continue;
    }
    const trimmed = line.trim();
    const ha = headingAlignRe.exec(trimmed);
    if (ha) {
      flushPara();
      blocks.push({
        type: "heading",
        level: Math.min(ha[1]!.length, 3) as 1 | 2 | 3,
        align: ha[2] as TextAlign,
        text: ha[3]!,
      });
      continue;
    }
    const h = headingRe.exec(trimmed);
    if (h) {
      flushPara();
      blocks.push({ type: "heading", level: Math.min(h[1]!.length, 3) as 1 | 2 | 3, text: h[2]! });
      continue;
    }
    const b = bulletRe.exec(trimmed);
    if (b) {
      flushPara();
      blocks.push({ type: "bullet", text: b[1]! });
      continue;
    }
    const pa = paraAlignRe.exec(trimmed);
    if (pa) {
      flushPara();
      paraAlign = pa[1] as TextAlign;
      para.push(pa[2]!);
      continue;
    }
    para.push(trimmed);
  }
  flushPara();
  return blocks;
}

const inlineRe = /(\*\*(.+?)\*\*|_(.+?)_|`([^`]+)`)/g;

/** Split a line into styled runs for native / HTML renderers. */
export function parseInlineRuns(text: string): InlineRun[] {
  const runs: InlineRun[] = [];
  let last = 0;
  for (const m of text.matchAll(inlineRe)) {
    const idx = m.index ?? 0;
    if (idx > last) runs.push({ text: text.slice(last, idx) });
    if (m[2]) runs.push({ text: m[2], bold: true });
    else if (m[3]) runs.push({ text: m[3], italic: true });
    else if (m[4]) runs.push({ text: m[4], code: true });
    last = idx + m[0].length;
  }
  if (last < text.length) runs.push({ text: text.slice(last) });
  if (!runs.length) runs.push({ text: text || "" });
  return runs;
}

function escapeHtml(text: string): string {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function inlineToHtml(text: string): string {
  return parseInlineRuns(text)
    .map((r) => {
      const inner = escapeHtml(r.text);
      if (r.bold) return `<strong>${inner}</strong>`;
      if (r.italic) return `<em>${inner}</em>`;
      if (r.code) return `<code>${inner}</code>`;
      return inner;
    })
    .join("");
}

/** HTML fragment for WebView / admin WYSIWYG (headings, paragraphs, bullet lists). */
export function markdownToHtml(source: string): string {
  if (!source.trim()) return "";
  const blocks = parseMarkdownBlocks(source);
  const parts: string[] = [];
  let inList = false;
  for (const b of blocks) {
    if (b.type === "bullet") {
      if (!inList) {
        parts.push("<ul>");
        inList = true;
      }
      parts.push(`<li>${inlineToHtml(b.text)}</li>`);
      continue;
    }
    if (inList) {
      parts.push("</ul>");
      inList = false;
    }
    if (b.type === "heading") {
      const tag = b.level === 1 ? "h2" : b.level === 2 ? "h3" : "h4";
      const style = b.align && b.align !== "left" ? ` style="text-align: ${b.align}"` : "";
      parts.push(`<${tag}${style}>${inlineToHtml(b.text)}</${tag}>`);
    } else {
      const style = b.align && b.align !== "left" ? ` style="text-align: ${b.align}"` : "";
      parts.push(`<p${style}>${inlineToHtml(b.text)}</p>`);
    }
  }
  if (inList) parts.push("</ul>");
  return parts.join("\n");
}
